// ================================================================
// 命盤分享設定對話框
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormGroup, FormControlLabel, Checkbox,
  Typography, Box, Alert, CircularProgress, Divider,
  IconButton, InputAdornment, Chip, Tooltip,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Visibility, VisibilityOff, ContentCopy, QrCode2 as QrCodeIcon,
  Delete as DeleteIcon, CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import {
  saveShareConfig, getShareConfig, revokeShare, regenerateShare,
  ShareField, SHARE_FIELD_OPTIONS, ShareConfig,
  SharePurpose, SHARE_PURPOSE_OPTIONS,
} from '../api/share';

interface Props {
  open: boolean;
  onClose: () => void;
}

const ShareSettingsDialog: React.FC<Props> = ({ open, onClose }) => {
  const [config, setConfig] = useState<ShareConfig>({ exists: false });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedFields, setSelectedFields] = useState<ShareField[]>(['nickname', 'gender', 'chart_grid']);
  const [sharePurpose, setSharePurpose] = useState<SharePurpose>('personal');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = config.share_token
    ? `${window.location.origin}/share/${config.share_token}`
    : '';

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getShareConfig();
      if (res.success && res.config.exists) {
        setConfig(res.config);
        if (res.config.shared_fields) setSelectedFields(res.config.shared_fields);
        if (res.config.share_purpose) setSharePurpose(res.config.share_purpose);
      }
    } catch {
      // 沒有設定也 OK
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setError(null);
      setSuccess(null);
      setPassword('');
      loadConfig();
    }
  }, [open, loadConfig]);

  const handleToggleField = (field: ShareField) => {
    setSelectedFields(prev =>
      prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field]
    );
  };

  const handleSave = async () => {
    const isExisting = config.exists && config.is_active;
    if (!isExisting && password.length < 4) {
      setError('密碼至少需要 4 個字元');
      return;
    }
    if (isExisting && password.length > 0 && password.length < 4) {
      setError('新密碼至少需要 4 個字元（留空則保留舊密碼）');
      return;
    }
    if (selectedFields.length === 0) {
      setError('請至少勾選一個分享項目');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await saveShareConfig(password, selectedFields, sharePurpose);
      if (res.success && res.share_token) {
        setConfig(prev => ({ ...prev, exists: true, share_token: res.share_token, is_active: true, shared_fields: selectedFields }));
        setPassword('');
        setSuccess('分享設定已儲存！');
      } else {
        setError(res.error || '儲存失敗');
      }
    } catch {
      setError('網路錯誤，請稍後再試');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm('確定要撤銷分享連結嗎？撤銷後朋友將無法查看。')) return;
    setRevoking(true);
    try {
      await revokeShare();
      setConfig({ exists: false });
      setSuccess('分享連結已撤銷');
    } catch {
      setError('撤銷失敗，請稍後再試');
    } finally {
      setRevoking(false);
    }
  };

  const handleRegenerate = async () => {
    if (password.length < 4) {
      setError('重新產生連結需要輸入新密碼（至少 4 個字元）');
      return;
    }
    if (!window.confirm('確定要重新產生連結嗎？\n\n舊的 QR code 和連結將立即失效，朋友需要使用新連結才能查看。')) return;
    setRegenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await regenerateShare(password, selectedFields, sharePurpose);
      if (res.success && res.share_token) {
        setConfig(prev => ({ ...prev, exists: true, share_token: res.share_token, is_active: true, view_count: 0, shared_fields: selectedFields }));
        setPassword('');
        setSuccess('新連結已產生，舊的 QR code 已失效。');
      } else {
        setError(res.error || '產生失敗，請稍後再試');
      }
    } catch {
      setError('網路錯誤，請稍後再試');
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <QrCodeIcon color="primary" />
        分享我的命盤
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

            {/* 目前分享狀態 */}
            {config.exists && config.is_active && config.share_token && (
              <Box>
                <Alert severity="success" icon={<CheckCircleIcon />}
                  sx={{ mb: 1 }}
                  action={
                    <Chip label={`已被查看 ${config.view_count ?? 0} 次`} size="small" />
                  }
                >
                  分享連結已啟用
                </Alert>

                {/* QR Code */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
                  <QRCodeSVG
                    value={shareUrl}
                    size={180}
                    level="M"
                    marginSize={2}
                    style={{ border: '4px solid white', borderRadius: 8 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center', maxWidth: 240, wordBreak: 'break-all' }}>
                    {shareUrl}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={copied ? <CheckCircleIcon color="success" /> : <ContentCopy />}
                    onClick={handleCopy}
                    sx={{ mt: 1 }}
                    variant="outlined"
                  >
                    {copied ? '已複製！' : '複製連結'}
                  </Button>
                </Box>
                <Divider sx={{ my: 2 }} />
              </Box>
            )}

            {/* 說明文字 */}
            <Typography variant="body2" color="text.secondary">
              朋友掃描 QR code 後，需輸入您設定的密碼才能查看命盤資料。
            </Typography>

            {/* 分享目的 */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>分享目的</Typography>
              <ToggleButtonGroup
                value={sharePurpose}
                exclusive
                onChange={(_, v) => { if (v) setSharePurpose(v as SharePurpose); }}
                size="small"
                sx={{ flexWrap: 'wrap', gap: 0.5 }}
              >
                {SHARE_PURPOSE_OPTIONS.map(opt => (
                  <ToggleButton key={opt.key} value={opt.key} sx={{ fontSize: '0.75rem', px: 1.5 }}>
                    {opt.icon} {opt.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {sharePurpose !== 'personal' && (
                <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                  對方查看後會看到「{SHARE_PURPOSE_OPTIONS.find(o => o.key === sharePurpose)?.ctaLabel}」按鈕
                </Typography>
              )}
            </Box>

            {/* 分享欄位勾選 */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>選擇要分享的資料</Typography>
              <FormGroup>
                {SHARE_FIELD_OPTIONS.map(opt => (
                  <FormControlLabel
                    key={opt.key}
                    control={
                      <Checkbox
                        checked={selectedFields.includes(opt.key)}
                        onChange={() => handleToggleField(opt.key)}
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">{opt.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{opt.description}</Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Box>

            <Divider />

            {/* 密碼設定 */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {config.exists && config.is_active ? '修改密碼（留空則保留舊密碼）' : '設定查看密碼'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                對方要輸入這個密碼才能看到你分享的命盤資料
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="查看密碼（至少 4 個字元）"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={config.exists && config.is_active ? '留空則保留現有密碼' : '請設定密碼'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size="small">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* 安全說明 */}
            {config.exists && config.is_active && (
              <Alert severity="info" sx={{ fontSize: '0.78rem' }}>
                <strong>安全選項：</strong><br />
                • <strong>更新設定</strong>：修改密碼或勾選項目，原 QR code 連結不變（舊密碼立即失效）<br />
                • <strong>重新產生連結</strong>：產生全新 QR code，舊連結立即失效（需輸入新密碼）<br />
                • <strong>撤銷分享</strong>：停止所有人查看，可隨時重新啟用
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, py: 2 }}>
        {/* 主要操作按鈕列 */}
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* 危險操作 */}
          {config.exists && config.is_active ? (
            <Tooltip title="停用後所有人立即無法查看">
              <Button
                color="error"
                variant="outlined"
                size="small"
                startIcon={revoking ? <CircularProgress size={16} /> : <DeleteIcon />}
                onClick={handleRevoke}
                disabled={revoking || saving || regenerating}
              >
                撤銷分享
              </Button>
            </Tooltip>
          ) : <Box />}

          {/* 一般操作 */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} color="inherit" size="small">關閉</Button>

            {/* 重新產生連結（只在已有分享時顯示） */}
            {config.exists && config.is_active && (
              <Tooltip title="產生全新 QR code，舊連結立即失效（需輸入新密碼）">
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={regenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
                    onClick={handleRegenerate}
                    disabled={saving || revoking || regenerating}
                  >
                    重新產生連結
                  </Button>
                </span>
              </Tooltip>
            )}

            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={saving || loading || revoking || regenerating}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {config.exists && config.is_active ? '更新設定' : '建立分享'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ShareSettingsDialog;
